import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import MissionData from "../../mission/Timesheet/MissionData";
import MissionSummaryService from "../../../services/MissionSummaryService";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tooltip
} from '@mui/material';
import Title from '../../../components/Title';
import IndemnitySettingService from '../../../services/IndemnitySettingService';
import { useParams, useNavigate } from "react-router-dom";
import LoadingButton from '@mui/lab/LoadingButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    borderRadius: '8px',
    marginTop: '2%',
  },
  tabsContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  tabPanel: {
    padding: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  amountCell: {
    fontWeight: 'bold',
  },
  dailyAmountCell: {
    fontWeight: '500',
    color: '#2e7d32',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  infoBox: {
    backgroundColor: '#e3f2fd',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  fixedAmount: {
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    textAlign: 'center',
    fontWeight: 'bold',
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  observationField: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
  successIcon: {
    fontSize: '64px',
    color: '#4caf50',
    marginBottom: theme.spacing(2),
  },
  errorIcon: {
    fontSize: '64px',
    color: '#f44336',
    marginBottom: theme.spacing(2),
  },
  pdfButton: {
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  excelButton: {
    backgroundColor: '#217346',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1a5c38',
    },
  },
  successDialog: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers de fusion (dates, regroupement)
// ─────────────────────────────────────────────────────────────

/**
 * Convertit une date "dd/mm/yyyy" en objet Date pour comparaison.
 * Retourne null si non parsable.
 */
const parseDateDMY = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;
  const [d, m, y] = parts.map(Number);
  if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
  return new Date(y, m - 1, d);
};

/**
 * Fusionne un ensemble de destinations en une seule ligne
 * par mission_timesheet_destination_id.
 *
 * ⚠️ CORRECTION : la nuitée (night_number) est PRISE EN BASE,
 * PAS cumulée. La nuitée est une propriété de la destination,
 * pas une quantité à sommer entre les doublons renvoyés par l'API.
 * On garde donc la première valeur significative rencontrée.
 */
const groupDestinationsByDestinationId = (destinations) => {
  if (!Array.isArray(destinations)) return [];

  const groups = new Map();

  destinations.forEach((dest) => {
    const key = dest?.mission_timesheet_destination_id ?? `unk-${Math.random()}`;

    if (!groups.has(key)) {
      groups.set(key, {
        mission_timesheet_destination_id: dest?.mission_timesheet_destination_id,
        commune_depart: dest?.commune_depart,
        commune_arrivee: dest?.commune_arrivee,
        daily_amount: 0,
        night_number: null, // sera renseigné à la première valeur valide
        date_depart: dest?.date_depart || null,
        date_fin: dest?.date_fin || null,
        indemnity_type: dest?.indemnity_type || '',
        indemnity_id: dest?.indemnity_id || null,
        missionary_id: dest?.missionary_id,
      });
    }

    const g = groups.get(key);

    // ⚠️ CORRECTION PRINCIPALE : NE PAS CUMULER LES NUITS.
    // On garde la première valeur significative trouvée en base.
    const newNight = dest?.night_number;
    if (
      newNight !== undefined &&
      newNight !== null &&
      String(newNight) !== "" &&
      (g.night_number === null || String(g.night_number) === "")
    ) {
      g.night_number = newNight;
    }

    // Taux journalier : on garde le premier non-nul trouvé
    const da = parseFloat(dest?.daily_amount) || 0;
    if (da > 0 && g.daily_amount === 0) {
      g.daily_amount = da;
    }

    // Fusion des dates (min / max)
    const dStart = parseDateDMY(dest?.date_depart);
    const dEnd   = parseDateDMY(dest?.date_fin);
    const gStart = parseDateDMY(g.date_depart);
    const gEnd   = parseDateDMY(g.date_fin);

    if (dStart && (!gStart || dStart < gStart)) {
      g.date_depart = dest.date_depart;
    }
    if (dEnd && (!gEnd || dEnd > gEnd)) {
      g.date_fin = dest.date_fin;
    }

    // Compléter les libellés manquants
    if (!g.commune_depart && dest?.commune_depart) g.commune_depart = dest.commune_depart;
    if (!g.commune_arrivee && dest?.commune_arrivee) g.commune_arrivee = dest.commune_arrivee;
    if (!g.indemnity_type && dest?.indemnity_type) g.indemnity_type = dest.indemnity_type;
    if (!g.indemnity_id && dest?.indemnity_id) g.indemnity_id = dest.indemnity_id;
  });

  // Normalisation : night_number par défaut à 0 si jamais renseigné
  return Array.from(groups.values()).map((g) => ({
    ...g,
    night_number: g.night_number !== null ? g.night_number : 0,
  }));
};

const IndemnityValidation = (props) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [detailmission, setDetailmission] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [type, setType] = useState("");
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const params = useParams();
  const mission_planning_id = props.mission_planning_id || params.mission_planning_id;
  const agenda_id = props.agenda_id || params.agenda_id;
  const [missionaryList, setMissionaryList] = useState([]);
  const [destinationsByUser, setDestinationsByUser] = useState({});
  const [mission, setMission] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const missionid = props.mission_planning_id || params.mission_planning_id;
  const [isloading, setIsloading] = useState(false);
  const [refuseLoading, setRefuseLoading] = useState(false);
  const timesheet_id = props.timesheet_id || params.timesheet_id;
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [indemnityData, setIndemnityData] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState({});

  const [openRefuseDialog, setOpenRefuseDialog] = useState(false);
  const [observation, setObservation] = useState('');
  const [observationError, setObservationError] = useState('');

  const formatDateDisplay = (dateDepart, dateFin) => {
    if (!dateDepart && !dateFin) return 'Non spécifié';
    if (dateDepart === dateFin) return dateDepart;
    return `${dateDepart} - ${dateFin}`;
  };

  const getDestinationsForUser = (userId) => {
    try {
      return destinationsByUser[userId] || [];
    } catch (error) {
      console.error('Erreur dans getDestinationsForUser:', error);
      return [];
    }
  };

  /**
   * ✅ Renvoie la liste des destinations regroupées (une seule ligne
   * par mission_timesheet_destination_id) pour un utilisateur donné.
   */
  const getGroupedDestinationsForUser = (userId) => {
    return groupDestinationsByDestinationId(getDestinationsForUser(userId));
  };

  const getDailyAmountForDestination = (destination) => {
    if (!destination) return 0;
    if (destination.daily_amount) return parseFloat(destination.daily_amount) || 0;
    if (destination.indemnity && destination.indemnity.daily_amount) {
      return parseFloat(destination.indemnity.daily_amount) || 0;
    }
    return 0;
  };

  const calculateRowAmount = (destination) => {
    try {
      const nightNumber = parseInt(destination?.night_number) || 0;
      const dailyAmount = getDailyAmountForDestination(destination);
      const numberOfDays = nightNumber === 0 ? 1 : nightNumber;
      return numberOfDays * dailyAmount;
    } catch (error) {
      console.error('Erreur dans calculateRowAmount:', error);
      return 0;
    }
  };

  const calculateUserTotal = (userId) => {
    try {
      // ✅ On utilise la liste regroupée pour ne pas compter deux fois
      const destinations = getGroupedDestinationsForUser(userId);
      return destinations.reduce((total, destination) => total + calculateRowAmount(destination), 0);
    } catch (error) {
      console.error('Erreur dans calculateUserTotal:', error);
      return 0;
    }
  };

  const calculateGeneralTotal = () => {
    try {
      if (!Array.isArray(missionaryList)) return 0;
      return missionaryList.reduce((total, user) => total + calculateUserTotal(user.user_id), 0);
    } catch (error) {
      console.error('Erreur dans calculateGeneralTotal:', error);
      return 0;
    }
  };

  // ✅ URL Excel construite à partir de l'ID selon le type
  const buildExcelUrl = () => {
    let excelFileName;
    if (type === "planning") {
      excelFileName = `recap_indemnites_${mission_planning_id}.xlsx`;
    } else {
      excelFileName = `recap_indemnites_${timesheet_id}.xlsx`;
    }
    return `${process.env.REACT_APP_API_URL_FILEPGI}/Indemnite/${excelFileName}`;
  };

  const handleExportExcel = () => {
    setDownloadingExcel(true);
    try {
      const excelUrl = buildExcelUrl();
      window.open(excelUrl, '_blank');
    } catch (error) {
      console.error("Erreur lors du téléchargement de l'Excel:", error);
      setErrorMessage("Erreur lors du téléchargement du fichier Excel");
      setOpenErrorDialog(true);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleDownloadPdfForMissionary = (missionary) => {
    if (!missionary || !missionary.last_name) {
      setErrorMessage("Nom du missionnaire manquant");
      setOpenErrorDialog(true);
      return;
    }

    setDownloadingPdf(prev => ({ ...prev, [missionary.user_id]: true }));

    try {
      let pdfFileName;
      if (type === "planning") {
        pdfFileName = `indemnite_${missionary.last_name}_${mission_planning_id}.pdf`;
      } else {
        pdfFileName = `indemnite_${missionary.last_name}_${timesheet_id}.pdf`;
      }
      const pdfUrl = `${process.env.REACT_APP_API_URL_FILEPGI}/Indemnite/${pdfFileName}`;
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Erreur lors du téléchargement du PDF:', error);
      setErrorMessage(`Erreur lors du téléchargement du PDF pour ${missionary.last_name || missionary.nom}`);
      setOpenErrorDialog(true);
    } finally {
      setDownloadingPdf(prev => ({ ...prev, [missionary.user_id]: false }));
    }
  };

  const handleOpenRefuseDialog = () => {
    setObservation('');
    setObservationError('');
    setOpenRefuseDialog(true);
  };

  const handleCloseRefuseDialog = () => {
    setOpenRefuseDialog(false);
    setObservation('');
    setObservationError('');
  };

  const handleToValidate = async () => {
    setIsloading(true);
    setError(null);
    try {
      const response = await IndemnitySettingService.validateindemnity(mission_planning_id, timesheet_id);
      if (response && response.status) {
        setSuccessMessage("Les indemnités ont été validées avec succès");
        setOpenSuccessDialog(true);
      } else {
        setErrorMessage("Une erreur est survenue lors de la validation des indemnités");
        setOpenErrorDialog(true);
      }
    } catch (error) {
      console.error('Erreur validation:', error);
      setErrorMessage("Une erreur est survenue lors de la validation des indemnités");
      setOpenErrorDialog(true);
    } finally {
      setIsloading(false);
    }
  };

  const handleRefuse = async () => {
    if (!observation.trim()) {
      setObservationError('Veuillez saisir une observation');
      return;
    }
    setRefuseLoading(true);
    setError(null);
    try {
      const response = await IndemnitySettingService.refusedvalidate(observation, mission_planning_id);
      if (response && response.status) {
        handleCloseRefuseDialog();
        setSuccessMessage("Les indemnités ont été refusées avec succès et les observations ont été envoyées au missionnaire.");
        setOpenSuccessDialog(true);
      } else {
        setErrorMessage("Une erreur est survenue lors du refus des indemnités");
        setOpenErrorDialog(true);
      }
    } catch (error) {
      console.error('Erreur refus:', error);
      setErrorMessage("Une erreur est survenue lors du refus des indemnités");
      setOpenErrorDialog(true);
    } finally {
      setRefuseLoading(false);
    }
  };

  const fetchMission = useCallback(async () => {
    if (!mission_planning_id || !agenda_id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await MissionSummaryService.getMissionData(mission_planning_id, agenda_id);
      if (response && response.status) {
        setDetailmission(response.data || []);
      } else {
        setDetailmission([]);
      }
    } catch (error) {
      console.error('Erreur fetchMission:', error);
      setDetailmission([]);
      setError("Erreur lors du chargement de la mission");
    } finally {
      setLoading(false);
    }
  }, [mission_planning_id, agenda_id]);

  useEffect(() => {
    fetchMission();
  }, [fetchMission]);

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
    navigate('/accounting/fuel_confirmed');
  };

  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
  };

  useEffect(() => {
    if (!missionid) return;
    setDataLoading(true);

    IndemnitySettingService.IndemnityDetail(missionid, timesheet_id).then((result) => {
      if (result && result.data) {
        setType(result.type);
      }
    });

    IndemnitySettingService.detailinvalidate(missionid, timesheet_id).then((result) => {
      if (result && result.data) {
        const missionaries = [];
        const destinationsMap = {};
        const indemnityMap = {};

        Object.keys(result.data).forEach(userId => {
          const userData = result.data[userId];

          missionaries.push({
            user_id: userData.user_id,
            nom: userData.nom,
            last_name: userData.last_name,
            function_code: userData.function_code
          });

          if (userData.destinations && Array.isArray(userData.destinations)) {
            destinationsMap[userId] = userData.destinations.map(dest => ({
              mission_timesheet_destination_id: dest.mission_timesheet_destination_id,
              commune_depart: dest.commune_depart,
              commune_arrivee: dest.commune_arrivee,
              night_number: dest.night_number,
              date_depart: dest.date_depart,
              date_fin: dest.date_fin,
              missionary_id: userId,
              daily_amount: dest.daily_amount || (dest.indemnity ? dest.indemnity.daily_amount : 0),
              indemnity_type: dest.indemnity_type || (dest.indemnity ? dest.indemnity.indemnity_type : ''),
              indemnity_id: dest.indemnity_id || (dest.indemnity ? dest.indemnity.indemnity_id : null)
            }));

            userData.destinations.forEach((dest, index) => {
              const key = `${userId}_${index}`;
              indemnityMap[key] = {
                daily_amount: dest.daily_amount || (dest.indemnity ? dest.indemnity.daily_amount : 0),
                indemnity_type: dest.indemnity_type || (dest.indemnity ? dest.indemnity.indemnity_type : ''),
                indemnity_id: dest.indemnity_id || (dest.indemnity ? dest.indemnity.indemnity_id : null)
              };
            });
          }
        });

        setState(result.state);
        setMissionaryList(missionaries);
        setDestinationsByUser(destinationsMap);
        setIndemnityData(indemnityMap);

        if (result.mission) setMission(result.mission);
      }
      setDataLoading(false);
    }).catch(error => {
      console.error('Erreur IndemnityDetail:', error);
      setError("Erreur lors du chargement des détails d'indemnité");
      setDataLoading(false);
    });
  }, [missionid, timesheet_id]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const hasData = missionaryList && missionaryList.length > 0;

  if (loading || dataLoading) {
    return (
      <Paper className={classes.paper} variant="outlined">
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <span style={{ marginLeft: '10px' }}>Chargement en cours...</span>
        </div>
      </Paper>
    );
  }

  return (
    <>
      <Paper className={classes.paper} variant="outlined">
        {error && (
          <Alert severity="error" style={{ marginBottom: '20px' }}>
            {error}
          </Alert>
        )}

        {detailmission && <MissionData mission={detailmission} />}

        <Box className={classes.tabsContainer}>
          {!hasData ? (
            <Box
              sx={{
                textAlign: "center",
                padding: "20px",
                marginTop: "20px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                border: "1px solid #e0e0e0"
              }}
            >
              <p style={{ fontWeight: "bold", fontSize: "16px", color: "#555" }}>
                L'indemnité de mission n'a pas encore été transmise pour validation.
              </p>
            </Box>
          ) : (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {(!state || state === "PENDING") && (
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <LoadingButton
                          variant="contained"
                          color="primary"
                          onClick={handleToValidate}
                          loading={isloading}
                        >
                          VALIDER
                        </LoadingButton>
                        <LoadingButton
                          variant="contained"
                          color="secondary"
                          onClick={handleOpenRefuseDialog}
                          loading={refuseLoading}
                        >
                          REFUSER
                        </LoadingButton>
                      </Box>
                    )}
                    {state === "CONFIRMED" && (
                      <Alert severity="success">Cette indemnité a déjà été validée</Alert>
                    )}
                    {state === "REFUSED" && (
                      <Alert severity="info">
                        Cette indemnité est en attente de modification par le missionnaire suite aux observations
                      </Alert>
                    )}
                  </Grid>
                </Grid>

                <Tabs value={selectedTab} onChange={handleTabChange} aria-label="indemnity tabs">
                  <Tab label="Récapitulatif" />
                  {missionaryList.map((user, index) => (
                    <Tab key={user?.user_id || index} label={user?.nom || `Missionnaire ${index + 1}`} />
                  ))}
                </Tabs>
              </Box>

              {/* Onglet Récapitulatif (index 0) */}
              <TabPanel value={selectedTab} index={0}>
                <Title>Récapitulatif des indemnités</Title>

                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Missionnaire</TableCell>
                        <TableCell>Total des indemnités</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {missionaryList.map((user, index) => {
                        const totalAmount = calculateUserTotal(user.user_id);
                        return (
                          <TableRow key={user?.user_id || index}>
                            <TableCell>
                              <strong>{user?.nom || ''} </strong>
                              {user?.function_code && (
                                <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '8px' }}>
                                  ({user.function_code})
                                </span>
                              )}
                            </TableCell>
                            <TableCell className={classes.amountCell}>
                              {totalAmount.toLocaleString('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })} AR
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setSelectedTab(index + 1)}
                                  style={{ marginRight: '8px' }}
                                >
                                  Voir détails
                                </Button>

                                <Tooltip title={`Télécharger le PDF pour ${user?.nom || ''} ${user?.last_name || ''}`}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={() => handleDownloadPdfForMissionary(user)}
                                    disabled={downloadingPdf[user.user_id]}
                                    className={classes.pdfButton}
                                    style={{ minWidth: '80px' }}
                                  >
                                    {downloadingPdf[user.user_id] ? (
                                      <CircularProgress size={20} color="inherit" />
                                    ) : (
                                      'PDF'
                                    )}
                                  </Button>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {missionaryList.length > 0 && (
                        <TableRow style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                          <TableCell>Total Général</TableCell>
                          <TableCell className={classes.amountCell}>
                            {calculateGeneralTotal().toLocaleString('fr-FR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })} AR
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Tooltip title="Télécharger le récapitulatif Excel">
                      <Button
                        variant="contained"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportExcel}
                        disabled={downloadingExcel || missionaryList.length === 0}
                        className={classes.excelButton}
                        style={{ minWidth: '200px' }}
                      >
                        {downloadingExcel ? 'Téléchargement...' : 'Récapitulatif Excel'}
                      </Button>
                    </Tooltip>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Onglets par missionnaire (index 1+) */}
              {missionaryList.map((user, userIndex) => {
                // ✅ liste regroupée : une seule ligne par mission_timesheet_destination_id
                const groupedDestinations = getGroupedDestinationsForUser(user.user_id);

                return (
                  <TabPanel
                    key={user?.user_id || userIndex}
                    value={selectedTab}
                    index={userIndex + 1}
                  >
                    <Title>{`Indemnités - ${user?.nom || ''} ${user?.last_name || ''}`}</Title>

                    <TableContainer component={Paper} className={classes.tableContainer}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Lieu de départ</TableCell>
                            <TableCell>Lieu d'arrivée</TableCell>
                            <TableCell>Nuitée</TableCell>
                            <TableCell>Indemnité unitaire (AR/jour)</TableCell>
                            <TableCell className={classes.amountCell}>Montant (AR)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupedDestinations.map((destination, destIndex) => {
                            const amount = calculateRowAmount(destination);
                            const dailyAmount = getDailyAmountForDestination(destination);

                            return (
                              <TableRow
                                key={
                                  destination?.mission_timesheet_destination_id ||
                                  `dest-${user.user_id}-${destIndex}`
                                }
                              >
                                <TableCell>
                                  {formatDateDisplay(destination?.date_depart, destination?.date_fin)}
                                </TableCell>
                                <TableCell>{destination?.commune_depart || 'N/A'}</TableCell>
                                <TableCell>{destination?.commune_arrivee || 'N/A'}</TableCell>
                                <TableCell>
                                  {destination?.night_number || '0'}
                                  {destination?.night_number === 0 && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="textSecondary"
                                      sx={{ fontStyle: 'italic' }}
                                    >
                                      (Déplacement journalier)
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell className={classes.dailyAmountCell}>
                                  <Box className={classes.fixedAmount}>
                                    {dailyAmount.toLocaleString('fr-FR', {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0
                                    })} AR
                                  </Box>
                                  {destination?.indemnity_type && (
                                    <Typography variant="caption" display="block" color="textSecondary">
                                      {destination.indemnity_type}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell className={classes.amountCell}>
                                  {amount.toLocaleString('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })} AR
                                </TableCell>
                              </TableRow>
                            );
                          })}

                          {groupedDestinations.length > 0 && (
                            <TableRow style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                              <TableCell colSpan={5} align="right">Total pour {user?.nom || ''}:</TableCell>
                              <TableCell className={classes.amountCell}>
                                {calculateUserTotal(user.user_id).toLocaleString('fr-FR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })} AR
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button variant="outlined" onClick={() => setSelectedTab(0)}>
                        Retour au récapitulatif
                      </Button>
                    </Box>
                  </TabPanel>
                );
              })}
            </>
          )}
        </Box>
      </Paper>

      {/* Dialogue de refus */}
      <Dialog open={openRefuseDialog} onClose={handleCloseRefuseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white' }}>
          Refuser l'indemnité de mission
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText sx={{ mb: 2 }}>
            Veuillez saisir une observation pour justifier le refus de cette indemnité de mission.
            Cette observation sera visible par le missionnaire.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="observation"
            label="Observation *"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={observation}
            onChange={(e) => {
              setObservation(e.target.value);
              if (observationError) setObservationError('');
            }}
            error={!!observationError}
            helperText={observationError}
            className={classes.observationField}
            placeholder="Saisissez ici les raisons du refus..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseRefuseDialog} variant="outlined">Annuler</Button>
          <LoadingButton
            onClick={handleRefuse}
            variant="contained"
            color="secondary"
            loading={refuseLoading}
          >
            Confirmer le refus
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Dialogue succès */}
      <Dialog open={openSuccessDialog} onClose={handleCloseSuccessDialog} maxWidth="sm" fullWidth>
        <DialogTitle style={{ backgroundColor: '#4caf50', color: 'white', textAlign: 'center' }}>
          Succès !
        </DialogTitle>
        <DialogContent className={classes.successDialog}>
          <CheckCircleIcon className={classes.successIcon} />
          <Typography variant="h6" style={{ marginBottom: '16px', color: '#2e7d32' }}>
            {successMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccessDialog} color="primary">Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue erreur */}
      <Dialog open={openErrorDialog} onClose={handleCloseErrorDialog} maxWidth="sm" fullWidth>
        <DialogTitle style={{ backgroundColor: '#f44336', color: 'white', textAlign: 'center' }}>
          Erreur
        </DialogTitle>
        <DialogContent className={classes.successDialog}>
          <ErrorIcon className={classes.errorIcon} />
          <Typography variant="h6" style={{ marginBottom: '16px', color: '#c62828' }}>
            Une erreur est survenue
          </Typography>
          <Typography variant="body2" style={{ marginBottom: '24px', color: '#666' }}>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseErrorDialog} color="primary">Fermer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IndemnityValidation;